import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { LoginService } from "../../services"
import ST0 from "../../utils/constants/session-storage-key.constant"
import {
  LoginRecordRequestBody,
  StatementServiceGetStatementByUuidRequestBody,
  ValidateLoginRequestBody,
  BypassLoginRequestBody,
} from "../../utils/type/login.type"

type LoginForm = {
  statementUuid: string
  nationalId: string
  dateOfBirth: string
}

type LoginState = {
  uuidExists?: boolean
  loginForm: LoginForm
  statementDetail: string
  loggedIn: boolean
  cardEligible?: boolean
  nationalIdCheck?: boolean
  dateOfBirthCheck?: boolean
  captchaValidateCheck?: boolean
  captchaValidateHelper?: boolean
  byPassLogin?: boolean
  isInitLoginAction: boolean
}

const errorMessage = "Internal Server Error"
export const initialState: LoginState = {
  uuidExists: undefined,
  loggedIn: false,
  loginForm: {
    statementUuid: "",
    nationalId: "",
    dateOfBirth: "",
  },
  statementDetail: "",
  cardEligible: undefined,
  nationalIdCheck: undefined,
  dateOfBirthCheck: undefined,
  byPassLogin: false,
  isInitLoginAction: false,
  captchaValidateCheck: true,
  captchaValidateHelper: false,
}

const existByUuid = createAsyncThunk(
  "statementService/existByUuid",
  async (uuid: string) => {
    const res = await LoginService.existByUuid(uuid)
    return res?.data
  }
)

const getStatementByUuid = createAsyncThunk(
  "statementService/getStatementByUuid",
  async (
    requestBody: StatementServiceGetStatementByUuidRequestBody,
    { rejectWithValue }
  ) => {
    try {
      const res = await LoginService.getStatementByUuid(requestBody)
      return res.data
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data.error || { message: "Unknown error" }
      )
    }
  }
)

const checkCardEligible = createAsyncThunk(
  "statementService/checkCardEligible",
  async (uuid: string, { rejectWithValue }) => {
    try {
      const response = await LoginService.checkCardEligible({ uuid })
      return response.data // 直接返回 response.data
    } catch (error) {
      return rejectWithValue({
        message: (error as Error).message || errorMessage,
      })
    }
  }
)

const bypassLogin = createAsyncThunk(
  "statementService/byPassLogin",
  async (requestBody: BypassLoginRequestBody) => {
    const { data } = await LoginService.byPassLogin(requestBody)
    return data
  }
)

const createLoginRecord = createAsyncThunk(
  "statementService/createLoginRecord",
  async (requestBody: LoginRecordRequestBody) => {
    const { data } = await LoginService.createLoginRecord(requestBody)
    return data
  }
)

const checkLogin = createAsyncThunk(
  "statementService/checkLogin",
  async (requestBody: ValidateLoginRequestBody) => {
    const { data } = await LoginService.validateLoginCredential(requestBody)
    return data
  }
)

const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    setStatementUuid(state, action: PayloadAction<string>) {
      state.loginForm.statementUuid = action.payload
    },
    setNationalId(state, action: PayloadAction<string>) {
      state.loginForm.nationalId = action.payload
    },
    setBirthDate(state, action: PayloadAction<string>) {
      state.loginForm.dateOfBirth = action.payload
    },
    setNationalIdAndBirthDateAndCheckLogin(
      state,
      action: PayloadAction<string[]>
    ) {
      const [nationalId, dateOfBirth] = action.payload
      state.loginForm.nationalId = nationalId
      state.loginForm.dateOfBirth = dateOfBirth
    },
    logout: (state) => {
      /**
       * init state
       */
      state.uuidExists = undefined
      state.loggedIn = false
      state.cardEligible = undefined
      state.byPassLogin = undefined
      state.nationalIdCheck = undefined
      state.dateOfBirthCheck = undefined
      state.captchaValidateCheck = true
      state.captchaValidateHelper = false
      state.statementDetail = ""
      state.loginForm.nationalId = ""
      state.loginForm.dateOfBirth = ""
      state.loginForm.statementUuid = ""
      state.isInitLoginAction = false
      sessionStorage.removeItem(ST0.RECORD_KEY)
      sessionStorage.removeItem(ST0.SUCCESS_UUID_KEY)
      sessionStorage.removeItem(ST0.SELECTED_UUID_KEY)
      sessionStorage.removeItem(ST0.NATIONAL_ID_KEY)
      sessionStorage.removeItem(ST0.DATE_OF_BIRTH_KEY)
    },
    init: (state) => {
      /**
       * init state
       */
      state.uuidExists = undefined
      state.loggedIn = false
      state.cardEligible = undefined
      state.byPassLogin = undefined
      state.nationalIdCheck = undefined
      state.dateOfBirthCheck = undefined
      state.captchaValidateCheck = undefined
      state.captchaValidateHelper = undefined
      state.statementDetail = ""
      state.loginForm.nationalId = ""
      state.loginForm.dateOfBirth = ""
      state.loginForm.statementUuid = ""
      state.isInitLoginAction = true
    },
    resetInitLoginActionFlag: (state) => {
      state.isInitLoginAction = false
    },
    setBypassLogin: (state) => {
      state.byPassLogin = false
    },
    setInitLoginActionFlag: (state) => {
      state.isInitLoginAction = true
    },
    resetCaptchaValidateHelper: (state) => {
      state.captchaValidateHelper = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(existByUuid.fulfilled, (state, action) => {
        const exist = action.payload?.payload?.exist

        if (exist !== undefined) {
          state.uuidExists = exist // Setting the state based on the existing value
        } else {
          // If it's undefined or doesn't exist, handle as necessary
          state.uuidExists = false // or whatever fallback logic desired
        }
      })
      .addCase(existByUuid.rejected, (state) => {
        state.uuidExists = false
      })
      .addCase(getStatementByUuid.fulfilled, (state, action) => {
        try {
          state.statementDetail = action.payload.payload.statementDetail
          sessionStorage.setItem(
            ST0.NATIONAL_ID_KEY,
            action.payload.payload.nationalId
          )
          state.loggedIn = true
        } catch (error) {
          state.loggedIn = false
        }
      })
      .addCase(getStatementByUuid.rejected, (state) => {
        state.loggedIn = false
      })
      .addCase(checkCardEligible.fulfilled, (state, action) => {
        state.cardEligible = action.payload.payload
      })
      .addCase(checkCardEligible.rejected, (state) => {
        state.cardEligible = false
      })
      .addCase(bypassLogin.fulfilled, (state, action) => {
        if (action?.payload?.payload?.bypass) {
          state.loginForm.statementUuid = action.meta.arg.uuid
          state.loginForm.nationalId = action.payload.payload.nationalId
          state.loginForm.dateOfBirth = action.payload.payload.dateOfBirth
          sessionStorage.setItem(ST0.SELECTED_UUID_KEY, action.meta.arg.uuid)
          sessionStorage.setItem(ST0.SUCCESS_UUID_KEY, action.meta.arg.uuid)
          sessionStorage.setItem(
            ST0.RECORD_KEY,
            String(action.payload.payload.recordId)
          )
        }
        if (action?.payload?.payload?.bypass !== undefined) {
          state.byPassLogin = action.payload.payload.bypass
        }
      })
      .addCase(checkLogin.fulfilled, (state, action) => {
        const payloadData = action.payload?.payload

        if (Array.isArray(payloadData) && payloadData.length === 2) {
          const [nationalIdCheck, dateOfBirthCheck] = payloadData

          state.nationalIdCheck =
            nationalIdCheck !== undefined ? nationalIdCheck : false
          state.dateOfBirthCheck =
            dateOfBirthCheck !== undefined ? dateOfBirthCheck : false
          state.captchaValidateCheck = true
        } else {
          state.nationalIdCheck = false
          state.dateOfBirthCheck = false
          state.captchaValidateCheck = false
        }
      })
      .addCase(checkLogin.rejected, (state, action) => {
        if (action && action.error && action.error.code) {
          const arr = action.error.code.split(",")
          state.captchaValidateCheck = !arr.includes("01500")
          state.captchaValidateHelper = arr.includes("01500")
          state.nationalIdCheck = !arr.includes("01501")
          state.dateOfBirthCheck = !arr.includes("01502")
        }
      })
      .addCase(createLoginRecord.fulfilled, (state, action) => {
        sessionStorage.setItem(
          ST0.SELECTED_UUID_KEY,
          state.loginForm.statementUuid
        )
        sessionStorage.setItem(
          ST0.SUCCESS_UUID_KEY,
          state.loginForm.statementUuid
        )
        sessionStorage.setItem(ST0.RECORD_KEY, String(action.payload.payload))
      })
  },
})

export const loginActions = {
  ...loginSlice.actions,
  existByUuid,
  getStatementByUuid,
  checkCardEligible,
  createLoginRecord,
  checkLogin,
  bypassLogin,
}

export default loginSlice.reducer
